/**
 * A amostra fica numa coluna fixa da tabela (2026-10-15), fora do item.
 *
 * Células compostas do Cadastro de Suprimentos (redesign 2026-09-15):
 * amostra de cor, item (nome + selos + código · especificação), tipo com
 * família, barra saldo × mínimo, último preço com fornecedor e situação com
 * dot. Usadas pela tabela, pelos cards do celular e pelo painel de detalhe —
 * uma peça, três lugares.
 */
import React from "react";
import { FiPackage } from "react-icons/fi";
import styled from "styled-components";

import { mq } from "../../../styles/canonical/breakpoints";
import { getToneTokens } from "../../../styles/shared";
import { formatarDataLocal, formatarReaisBR } from "../../../utils/dinheiro";
import { Mono } from "../_shared/quadro/styles";
import { corDoItem, qtd, semPreco } from "./derivados";

// ---------------------------------------------------------------- amostra

const SwatchBox = styled.span`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: ${({ $size }) => ($size >= 48 ? 12 : 8)}px;
  flex: 0 0 auto;
  border: 1px solid var(--border-strong);
  background: ${({ $cor, $textura }) => ($textura ? `center / cover no-repeat url("${$textura}")` : $cor || "var(--surface-3)")};
  color: var(--ink-3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Amostra do padrão: a TEXTURA quando existe (2026-10-15), senão a cor;
 * ferragens e afins mostram o glifo.
 */
export function Swatch({ produto, size = 34 }) {
  const textura = produto.textura_miniatura_url || produto.textura_url || null;
  const cor = textura ? null : corDoItem(produto);
  const nomeDaCor = (produto.atributos || {}).cor;
  const titulo = textura ? (nomeDaCor ? `Textura · ${nomeDaCor}` : "Textura") : cor ? nomeDaCor : "Sem amostra de cor";
  return (
    <SwatchBox $size={size} $cor={cor} $textura={textura} title={titulo} aria-hidden="true">
      {textura || cor ? null : <FiPackage size={Math.round(size * 0.42)} />}
    </SwatchBox>
  );
}

// ---------------------------------------------------------------- selos

const Selo = styled.span`
  flex: 0 0 auto;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 17px;
  padding: 0 7px;
  border-radius: 999px;
  border: 1px solid ${({ $tone }) => getToneTokens(null, $tone).border};
  background: ${({ $tone }) => getToneTokens(null, $tone).background};
  color: ${({ $tone }) => ($tone === "neutral" ? "var(--ink-3)" : getToneTokens(null, $tone).text)};
  white-space: nowrap;
  @media ${mq.mobile} {
    font-size: var(--text-min);
    line-height: 19px;
  }
`;

export function SelosDoItem({ produto, semFornecedor }) {
  return (
    <>
      {!produto.ativo ? <Selo $tone="neutral" title="Inativo: não aparece nas listas operacionais.">inativo</Selo> : null}
      {semPreco(produto) ? (
        <Selo $tone="warning" title="Sem custo médio: entra no orçamento com custo zero até alguém informar o preço.">sem preço</Selo>
      ) : null}
      {semFornecedor && produto.ativo ? <Selo $tone="neutral" title="Nenhum fornecedor vinculado: não entra na cotação.">s/ fornecedor</Selo> : null}
      {produto.pendente_cadastro ? <Selo $tone="warning" title="Cadastro incompleto vindo do CAD.">aguardando cadastro</Selo> : null}
    </>
  );
}

// ---------------------------------------------------------------- item

const ItemWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  > .texto { min-width: 0; flex: 1; }
  > .texto > .nome { display: flex; align-items: center; gap: 8px; min-width: 0; }
  > .texto > .nome > span:first-child { font-weight: 600; font-size: 13.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  > .texto > .sub { font-size: 11.5px; color: var(--ink-3); margin-top: 2px; display: flex; gap: 6px; align-items: center; min-width: 0; }
  > .texto > .sub > .spec { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

export function CelulaItem({ produto, especificacao, semFornecedor }) {
  return (
    <ItemWrap>
      <div className="texto">
        <div className="nome">
          <span title={produto.nome}>{produto.nome}</span>
          <SelosDoItem produto={produto} semFornecedor={semFornecedor} />
        </div>
        <div className="sub">
          <Mono style={{ color: "var(--ink-2)", flex: "0 0 auto" }}>{produto.codigo}</Mono>
          <span>·</span>
          <span className="spec" title={especificacao}>{especificacao}</span>
        </div>
      </div>
    </ItemWrap>
  );
}

// ---------------------------------------------------------------- tipo

export function CelulaTipo({ nome, familia }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={nome}>{nome}</div>
      {familia ? <div style={{ fontSize: 11, color: "var(--ink-3)" }}>{familia}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- saldo

const COR_BARRA = {
  danger: "var(--danger-solid)",
  warning: "var(--warning-solid)",
  success: "var(--success-solid)",
  neutral: "var(--border-strong)",
};

/** Texto de apoio das células (11px no desktop; piso 12px no celular). */
const Sub = styled.span`
  font-size: 11px;
  color: var(--ink-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media ${mq.mobile} {
    font-size: var(--text-min);
  }
`;

const Barra = styled.div`
  height: 4px;
  border-radius: 2px;
  background: var(--surface-3);
  margin-top: 5px;
  overflow: hidden;
  > span { display: block; height: 100%; border-radius: 2px; transition: width .2s ease; }
`;

/** Linha `saldo` + `mín X`, barra saldo/mín (até 100%) e reservas. */
export function BarraSaldo({ info, saldoNegativo = false }) {
  return (
    <div style={{ minWidth: 0 }} data-testid="barra-saldo" data-tone={info.tone}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, fontSize: 13 }}>
        <span
          style={{ fontWeight: 600, color: info.abaixo || saldoNegativo ? "var(--danger-text)" : "var(--ink)" }}
          title={saldoNegativo ? "Consumo registrado sem entrada — lance a entrada." : undefined}
        >
          {qtd(info.saldo)}
        </span>
        <Sub>mín {qtd(info.min)}</Sub>
      </div>
      <Barra aria-hidden="true">
        <span style={{ width: `${info.pct}%`, background: COR_BARRA[info.tone] }} />
      </Barra>
      <Sub as="div" style={{ marginTop: 3 }}>{info.sub}</Sub>
    </div>
  );
}

// ---------------------------------------------------------------- preço

export function CelulaUltimoPreco({ ultimo }) {
  if (!ultimo) {
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-4)" }}>—</div>
        <Sub as="div">sem preço de fornecedor</Sub>
      </div>
    );
  }
  const sub = [ultimo.fornecedor, ultimo.data ? formatarDataLocal(ultimo.data) : ""].filter(Boolean).join(" · ");
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{formatarReaisBR(ultimo.valor)}</div>
      <Sub as="div" title={sub}>{sub}</Sub>
    </div>
  );
}

// ---------------------------------------------------------------- situação

export function DotSituacao({ ativo }) {
  const tone = ativo ? "success" : "neutral";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: ativo ? "var(--success-text)" : "var(--ink-3)" }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: getToneTokens(null, tone).accent, flex: "0 0 auto" }} />
      {ativo ? "Ativo" : "Inativo"}
    </span>
  );
}
